import { Intercom } from '@capacitor-community/intercom';
import { Capacitor } from '@capacitor/core';

// Services
import { captureException, replay } from './sentry';
import { INTERCOM_APP_ID } from '../env';
import { SUPPORT_EMAIL } from '../utils';

interface IntercomTraits {
  userId?: string;
  userHash?: string;
  email?: string;
  phone?: string;
  name?: string;
  customAttributes?: {
    [key: string]: any;
  };
}

const getSentryReplayAttributes = () => {
  const replayId = replay.getReplayId();
  return replayId ? { session_replay: `https://solve-finance.sentry.io/replays/${replayId}/` } : {};
};

// Last-resort escape hatch when the chat widget is unavailable: open the user's
// mail client to the support address so they always have a way to reach us.
const openEmailSupport = () => {
  try {
    window.location.href = `mailto:${SUPPORT_EMAIL}`;
  } catch (e) {
    captureException(e);
  }
};

let pendingChatPoll = false;

let isIdentified = false;

const waitForIntercomThenShow = () => {
  // Only one poller at a time so repeated clicks don't stack timers.
  if (pendingChatPoll) return;
  pendingChatPoll = true;

  const startedAt = Date.now();
  const TIMEOUT_MS = 8000;
  const POLL_MS = 250;

  const poll = () => {
    if (typeof window.Intercom === 'function') {
      pendingChatPoll = false;
      window.Intercom('show');
      return;
    }
    if (Date.now() - startedAt >= TIMEOUT_MS) {
      // Widget never loaded (blocked, or destination disabled in this env).
      // Fall back to email so the user isn't left with a dead button.
      pendingChatPoll = false;
      openEmailSupport();
      return;
    }
    window.setTimeout(poll, POLL_MS);
  };

  poll();
};

/**
 * Open the support chat from anywhere in the app.
 *
 * - Web: shows the Intercom messenger injected by Segment's device-mode
 *   destination (`window.Intercom`). That widget loads asynchronously, so if a
 *   user clicks before it's ready we poll briefly and show it once it arrives.
 * - Native: uses the Capacitor Intercom plugin.
 *
 * Call sites used to inline `window?.Intercom ? window.Intercom('show') :
 * Intercom.displayMessenger()`. On web the `displayMessenger()` fallback hits
 * the Capacitor web stub, which throws "Not implemented on web." — so the
 * button silently did nothing whenever the widget hadn't loaded yet (slow
 * load, tracking blocker, destination disabled).
 */
export const openSupportChat = () => {
  if (typeof window !== 'undefined' && typeof window.Intercom === 'function') {
    window.Intercom('show');
    return;
  }

  if (Capacitor.isNativePlatform()) {
    Intercom.displayMessenger().catch((e) => {
      captureException(e);
      openEmailSupport();
    });
    return;
  }

  waitForIntercomThenShow();
};

export const updateIntercom = (traits: IntercomTraits) => {
  try {
    const { userId, userHash, customAttributes, ...rest } = traits;
    const mergedCustomAttributes = { ...getSentryReplayAttributes(), ...customAttributes };

    if (window?.Intercom && !Capacitor.isNativePlatform()) {
      if (userId && userHash) {
        isIdentified = true;
        window.Intercom('shutdown');
        window.Intercom('boot', {
          app_id: INTERCOM_APP_ID,
          user_id: userId,
          user_hash: userHash,
          ...rest,
          ...mergedCustomAttributes,
        });
      } else {
        window.Intercom('update', {
          ...(isIdentified ? rest : {}),
          ...mergedCustomAttributes,
        });
      }
    } else if (Intercom && Capacitor.isNativePlatform()) {
      // On native, the hash must be set before identification / update so that
      // subsequent calls are authenticated.
      if (userHash) {
        Intercom.setUserHash({ hmac: userHash }).catch(captureException);
      }
      Intercom.updateUser({ ...rest, ...(userId ? { userId } : {}), customAttributes: mergedCustomAttributes });
    }
  } catch (e) {
    captureException(e);
  }
};
